import { ActionIcon, Anchor, Card, Group, Stack, Text } from '@mantine/core'
import { modals } from '@mantine/modals'
import type { AttachmentResponse } from '@markku/types'
import { partition, sortBy } from '@markku/utils'
import { IconFile, IconTrash } from '@tabler/icons-react'
import { useState } from 'react'
import type { AttachmentHooks } from '../attachments/hooks'

import { PhotoGallery } from '../common/PhotoGallery'
import { useAttachmentUpload } from './useAttachmentUpload'

export type AttachmentCategory =
  | 'general'
  | 'location'
  | 'building'
  | 'systems'
  | 'roof'
  | 'exterior'
  | 'documents'
  | 'warranty'

interface CategoryAttachmentsProps {
  category: AttachmentCategory
  attachments?: AttachmentResponse[]
  isLoading?: boolean
  canEdit?: boolean
  propertyId: number
  propertyHooks: {
    useUpdate: () => {
      mutateAsync: (input: {
        id: number
        data: Record<string, unknown>
        attachments?: Array<
          | { id: number; action: 'add' | 'delete' }
          | {
              id: number
              action: 'update'
              data: { description: string | null }
            }
        >
      }) => Promise<unknown>
      isPending: boolean
    }
  }
  attachmentHooks: AttachmentHooks
  allowDocuments?: boolean
  compact?: boolean
  showInlineButton?: boolean
  renderAddButton?: (props: {
    onClick: () => void
    loading: boolean
    accept?: string
  }) => React.ReactNode
}

export function CategoryAttachments({
  category,
  attachments,
  canEdit = false,
  propertyId,
  propertyHooks,
  attachmentHooks,
  allowDocuments = false,
  compact = false,
  renderAddButton,
}: CategoryAttachmentsProps) {
  const [fileInputElement, setFileInputElement] =
    useState<HTMLInputElement | null>(null)

  const { handleUpload, uploading } = useAttachmentUpload({
    category,
    propertyId,
    allowDocuments,
    propertyHooks,
    attachmentHooks,
  })

  const { mutateAsync: updateProperty } = propertyHooks.useUpdate()

  const triggerFileSelect = () => {
    fileInputElement?.click()
  }

  // Filter attachments by category
  const categoryAttachments =
    attachments?.filter((a) => a.metadata?.category === category) || []

  const sortedAttachments = sortBy(categoryAttachments, [
    (a) => a.createdAt,
    'asc',
  ])

  // Separate images and documents
  const [images, documents] = partition(sortedAttachments, (a) => {
    // For document section we list also photos as attachments
    if (allowDocuments) {
      return false
    }
    return a.mimeType.startsWith('image/')
  })

  const handleDeleteImage = async (attachmentId: number) => {
    await updateProperty({
      id: propertyId,
      data: {},
      attachments: [{ id: attachmentId, action: 'delete' }],
    })
  }

  const handleUpdateDescription = async (id: number, description: string) => {
    await updateProperty({
      id: propertyId,
      data: {},
      attachments: [
        { id, action: 'update', data: { description: description || null } },
      ],
    })
  }

  const handleDeleteDocument = (attachmentId: number) => {
    modals.openConfirmModal({
      title: 'Poista tiedosto',
      centered: true,
      children: <Text>Haluatko varmasti poistaa tämän tiedoston?</Text>,
      labels: { confirm: 'Poista', cancel: 'Peruuta' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        await handleDeleteImage(attachmentId)
      },
    })
  }

  const hasAttachments = categoryAttachments.length > 0
  const accept = allowDocuments ? undefined : 'image/*,android/allowCamera'

  if (!hasAttachments && !canEdit) {
    return null
  }

  return (
    <>
      {/* Hidden file input - for documents only */}
      {allowDocuments && (
        <input
          ref={setFileInputElement}
          type="file"
          accept={accept}
          onChange={(e) => {
            const file = e.target.files?.[0]
            if (file) {
              handleUpload(file)
              e.target.value = '' // Reset for re-selection
            }
          }}
          style={{ display: 'none' }}
        />
      )}

      {/* Render custom add button if provided (outside Stack so parent can position it) */}
      {canEdit &&
        renderAddButton &&
        renderAddButton({
          onClick: triggerFileSelect,
          loading: uploading,
          accept,
        })}

      <Stack gap="xs">
        {/* Images */}
        {images.length > 0 && (
          <PhotoGallery
            photos={images}
            compact={compact}
            onUpdate={canEdit ? handleUpdateDescription : undefined}
            onDelete={canEdit ? handleDeleteImage : undefined}
          />
        )}

        {/* Documents */}
        {documents.length > 0 && (
          <Stack gap="xs">
            {documents.map((attachment) => (
              <Card key={attachment.id} withBorder p="xs">
                <Group justify="space-between" wrap="nowrap">
                  <Anchor
                    href={attachment.downloadUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Group gap="xs" style={{ flex: 1, minWidth: 0 }}>
                      <IconFile size={20} />
                      <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
                        <Text size="sm" truncate>
                          {attachment.description || attachment.filename}
                        </Text>
                        {attachment.description && (
                          <Text size="xs" c="dimmed" truncate>
                            {attachment.filename}
                          </Text>
                        )}
                      </Stack>
                    </Group>
                  </Anchor>
                  {canEdit && (
                    <Group gap={4}>
                      <ActionIcon
                        size="sm"
                        variant="subtle"
                        color="red"
                        onClick={() => handleDeleteDocument(attachment.id)}
                      >
                        <IconTrash size={14} />
                      </ActionIcon>
                    </Group>
                  )}
                </Group>
              </Card>
            ))}
          </Stack>
        )}

        {!hasAttachments && (
          <Stack align="center" h="100%">
            <Text size="sm" c="dimmed">
              Ei lisättyjä tiedostoja
            </Text>
          </Stack>
        )}
      </Stack>
    </>
  )
}
